import SocketService, { NAMESPACES, SocketRequest } from '../SocketService';

// Singleton instance
let instance = null;

export class MessagesSocketService {
    constructor() {
        if (instance) {
            return instance;
        }

        this.socket = new SocketService(NAMESPACES.MESSAGES);
        this.connectionPromise = null;

        this.listeners = {
            onMessageReceived: [],
            onUserTyping: [],
            onConversationLeft: [],
            onConversationJoined: [],
            onConversationUpdated: [],
            onDisconnected: [],
            onMessageStatus: [],
            onMessageReaction: [],
            onMessageEdited: [],
            onMessageDeleted: [],
            onReconnected: [],
        };

        // Store cleanup functions
        this.cleanupFns = [];
        
        // Track active conversations to re-join on reconnect
        this.activeConversations = new Set();
        
        // DON'T auto-connect - wait for explicit connect call
        
        instance = this;
    }

    static getInstance() {
        if (!instance) {
            instance = new MessagesSocketService();
        }
        return instance;
    }

    setListeners(listeners) {
        this.listeners = { ...this.listeners, ...listeners };
    }

    async ensureConnected() {
        if (!this.connectionPromise) {
            this.connectionPromise = this.socket.connect()
                .then(() => {
                    this.setupEventListeners();
                    return this.rejoinActiveConversations();
                })
                .catch(error => {
                    console.error('Failed to connect messages socket:', error);
                    this.connectionPromise = null;
                    throw error;
                });
        }
        return this.connectionPromise;
    }

    async connect() {
        return this.ensureConnected();
    }

    async disconnect(destroy = false) {
        try {
            // Clean up all listeners
            this.cleanupFns.forEach(cleanup => cleanup());
            this.cleanupFns = [];
            
            await this.socket.disconnect();
            this.connectionPromise = null;
            
            if (destroy) {
                instance = null;
            }
        } catch (error) {
            console.error('Error disconnecting socket:', error);
            throw error;
        }
    }

    emit(event, data) {
        return this.socket.emit(event, data);
    }

    on(event, callback) {
        const cleanup = this.socket.on(event, callback);
        this.cleanupFns.push(cleanup);
        return cleanup;
    }

    triggerListener(listener, data) {
        if (!this.listeners[listener]) {
            return;
        }

        if (listener === 'onUserTyping') {
            console.log('Triggering onUserTyping listener with data:', data);
        }
        
        if (!Array.isArray(this.listeners[listener])) {
            return;
        }
        
        this.listeners[listener].forEach(({ callback }) => {
            try {
                callback(data);
            } catch (error) {
            }
        });
    }

    generateID() {
        return Math.random().toString(36).substring(2, 9);
    }

    addListener(listener, callback) {
        const id = this.generateID();
        if (!this.listeners[listener]) {
            this.listeners[listener] = [];
        }
        this.listeners[listener].push({
            id,
            callback
        });
        return id;
    }

    removeListener(listener, id) {
        if (!this.listeners[listener]) {
            return;
        }
        this.listeners[listener] = this.listeners[listener].filter(({ id: listenerId }) => listenerId !== id);
    }

    setupEventListeners() {
        console.log('🔧 Setting up event listeners for MessagesSocketService...');
        
        // Don't setup if already setup
        if (this.cleanupFns.length > 0) {
            console.log('ℹ️ Event listeners already setup, skipping...');
            return;
        }

        // Set up message event listener
        const cleanupMessage = this.on('conversation', (data) => {
            const request = SocketRequest.fromObject(data);
            const safeContent = (request.content && typeof request.content === 'object') ? request.content : {};

            try {
                console.log('[MessagesSocketService] ⬇️ conversation event:', {
                    event: request.event,
                    conversationId: request.conversationId,
                    contentId: safeContent?.id || safeContent?.messageId,
                    timestamp: new Date().toISOString(),
                });
            } catch (e) {}

            switch (request.event) {
                case 'message':
                    console.log('[MessagesSocketService] 📨 onMessageReceived triggered');
                    this.triggerListener('onMessageReceived', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:reaction':
                    console.log('[MessagesSocketService] 😀 onMessageReaction triggered', safeContent);
                    this.triggerListener('onMessageReaction', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:edited':
                    console.log('[MessagesSocketService] ✏️ onMessageEdited triggered');
                    this.triggerListener('onMessageEdited', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:deleted':
                    console.log('[MessagesSocketService] 🗑️ onMessageDeleted triggered');
                    this.triggerListener('onMessageDeleted', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'typing':
                    this.triggerListener('onUserTyping', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'leave':
                case 'left':
                    this.triggerListener('onConversationLeft', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'joined':
                    this.triggerListener('onConversationJoined', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'updated':
                    this.triggerListener('onConversationUpdated', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:status':
                case 'status':
                    this.triggerListener('onMessageStatus', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:pinned':
                    console.log('[MessagesSocketService] 📌 onMessagePinned triggered');
                    this.triggerListener('onMessagePinned', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                case 'message:unpinned':
                    console.log('[MessagesSocketService] 📍 onMessageUnpinned triggered');
                    this.triggerListener('onMessageUnpinned', {
                        ...safeContent,
                        conversationId: request.conversationId
                    });
                    break;
                default:
                    console.warn('Unknown conversation event:', request.event);
            }
        });
        this.cleanupFns.push(cleanupMessage);

        // Set up disconnect listener
        const cleanupDisconnect = this.on('disconnect', (reason) => {
            console.log('Messages socket disconnected. Reason:', reason);
            this.connectionPromise = null;
            this.triggerListener('onDisconnected', { reason });
        });
        this.cleanupFns.push(cleanupDisconnect);

        // Set up reconnect listener
        const cleanupReconnect = this.on('reconnect', (attemptNumber) => {
            console.log(`Messages socket reconnected after ${attemptNumber} attempts`);
            this.rejoinActiveConversations()
                .then(() => {
                    this.triggerListener('onReconnected', { attemptNumber });
                })
                .catch(error => {
                    console.error('Failed to rejoin conversations after reconnect:', error);
                });
        });
        this.cleanupFns.push(cleanupReconnect);
        
        console.log('✅ Event listeners setup complete for MessagesSocketService');
    }

    async sendMessage(conversationId, messageOrId) {
        await this.ensureConnected();
        
        // Support both message object and messageId string for backward compatibility
        const content = typeof messageOrId === 'string' 
            ? { messageId: messageOrId }
            : messageOrId;
        
        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'message',
            content
        );
        
        this.emit('conversation', request.toObject());
    }

    async reactToMessage(conversationId, messageId, emoji, action = 'add') {
        await this.ensureConnected();

        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'message:reaction',
            { messageId, emoji, action }
        );

        this.emit('conversation', request.toObject());
    }

    async setTyping(conversationId, data) {
        await this.ensureConnected();

        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'typing',
            data
        );
        
        this.emit('conversation', request.toObject());
    }

    async setMessageStatus(conversationId, messageId, status) {
        await this.ensureConnected();
        
        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'status',
            { messageId, status }
        );
        
        this.emit('conversation', request.toObject());
    }

    async joinConversation(conversationId) {
        await this.ensureConnected();
        
        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'join',
            null
        );
        
        this.activeConversations.add(conversationId);
        this.emit('conversation', request.toObject());
    }

    async leaveConversation(conversationId) {
        await this.ensureConnected();
        
        const request = new SocketRequest(
            this.socket.socket?.id,
            conversationId,
            'leave',
            null
        );
        
        this.activeConversations.delete(conversationId);
        this.emit('conversation', request.toObject());
    }

    async rejoinActiveConversations() {
        if (this.activeConversations.size === 0) {
            return;
        }

        console.log(`Rejoining ${this.activeConversations.size} active conversations...`);
        
        const joinPromises = Array.from(this.activeConversations).map(conversationId => {
            const request = new SocketRequest(
                this.socket.socket?.id,
                conversationId,
                'join',
                null
            );
            return this.emit('conversation', request.toObject());
        });

        try {
            await Promise.all(joinPromises);
            console.log('✅ Successfully rejoined all active conversations');
        } catch (error) {
            console.error('Failed to rejoin some conversations:', error);
            throw error;
        }
    }
}

export default MessagesSocketService;